import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

interface UserDailyActivityResponse {
	role?: string;
	// Add other properties as needed
}

/**
 * Hook for checking if the current user has manager permissions (admin OR manager role)
 * This matches the backend logic from RepCoachingGoalsService.CanReadOrModifyAsync
 */
export function useManagerPermissions() {
	const userSession = useUserSession();
	const isAdmin = userSession.account.isAdmin;

	const { data: currentDayActivity, isLoading } = useQuery({
		queryKey: ['currentDayActivity', userSession.user.id],
		queryFn: async (): Promise<UserDailyActivityResponse | null> => {
			if (isAdmin) {
				// Skip API call if already admin
				return null;
			}

			try {
				const today = new Date().toISOString().split('T')[0]; // Format: yyyy-MM-dd
				const response = await userSession.webServiceHelper.callWebServiceAsync<UserDailyActivityResponse>(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `userDailyActivity/summary?userId=${userSession.user.id}&startDate=${today}&endDate=${today}`,
					}),
					'GET'
				);

				return response.value || null;
			} catch (error) {
				console.error('Failed to fetch current day activity:', error);
				return null;
			}
		},
		enabled: !!userSession.user.id && !isAdmin, // Only fetch if not admin and user ID exists
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
	});

	// User has manager permissions if they are admin OR have MGR role
	const hasManagerPermissions = isAdmin || currentDayActivity?.role === 'MGR';

	return {
		hasManagerPermissions,
		isLoading: !isAdmin && isLoading, // Don't show loading if already admin
		isAdmin,
		isManager: currentDayActivity?.role === 'MGR',
	};
}
